/**
 * CR470: S5.1 Workflow Pack Selector
 * Dropdown to select a WORKFLOW prompt pack for the snippet locator step.
 * Persists selection in cr075_batch_runs.config_json.s5_1
 */
import { useState, useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Package, AlertCircle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface WorkflowPack {
  id: string;
  name: string;
  version: number;
  usage_stage: string | null;
  is_active: boolean;
}

interface S5WorkflowPackSelectorProps {
  batchRunId: string;
  currentConfig?: Record<string, unknown>;
  onPackSelected?: (packId: string | null) => void;
}

export function S5WorkflowPackSelector({ batchRunId, currentConfig, onPackSelected }: S5WorkflowPackSelectorProps) {
  const [packs, setPacks] = useState<WorkflowPack[]>([]);
  const [selectedPackId, setSelectedPackId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Derive current selection from config
  useEffect(() => {
    const s51 = (currentConfig as any)?.s5_1;
    if (s51?.prompt_pack_id) {
      setSelectedPackId(s51.prompt_pack_id);
    }
  }, [currentConfig]);

  // Fetch available WORKFLOW packs
  useEffect(() => {
    async function fetchPacks() {
      setLoading(true);
      const { data, error } = await supabase
        .from("prompt_packs")
        .select("id, name, version, usage_stage, is_active")
        .eq("pack_kind", "WORKFLOW")
        .is("archived_at", null)
        .order("created_at", { ascending: false });

      if (!error && data) {
        setPacks(data as WorkflowPack[]);
        // Auto-select first active pack if none selected
        if (!selectedPackId && data.length > 0) {
          const active = data.find(p => p.is_active);
          if (active) {
            setSelectedPackId(active.id);
          }
        }
      }
      setLoading(false);
    }
    fetchPacks();
  }, []);

  const handleSelect = async (packId: string) => {
    setSelectedPackId(packId);
    onPackSelected?.(packId);

    // Persist to batch run config_json.s5_1
    try {
      const { data: run } = await supabase
        .from("cr075_batch_runs")
        .select("config_json")
        .eq("id", batchRunId)
        .single();

      const config = (run?.config_json as Record<string, unknown>) || {};
      const updated = {
        ...config,
        s5_1: {
          prompt_pack_id: packId,
          mode: "workflow_pack",
          model_override: null,
        },
      };

      await supabase
        .from("cr075_batch_runs")
        .update({ config_json: updated })
        .eq("id", batchRunId);
    } catch (err) {
      console.error("Failed to persist S5.1 config:", err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Package className="h-4 w-4" />
        Loading packs...
      </div>
    );
  }

  if (packs.length === 0) {
    return (
      <div className="flex items-start gap-2 p-2 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 text-xs text-amber-800 dark:text-amber-300">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <span>No WORKFLOW prompt packs found. Create one in Admin → Prompt Packs with kind=WORKFLOW and usage_stage=S1_4_LOCATOR.</span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground flex items-center gap-1.5">
        <Package className="h-3.5 w-3.5" />
        Workflow Pack
      </Label>
      <Select value={selectedPackId || ""} onValueChange={handleSelect}>
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="Select a workflow pack..." />
        </SelectTrigger>
        <SelectContent>
          {packs.map(pack => (
            <SelectItem key={pack.id} value={pack.id} className="text-xs">
              <div className="flex items-center gap-2">
                <span>{pack.name}</span>
                <span className="text-muted-foreground">v{pack.version}</span>
                {pack.is_active && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0">Active</Badge>
                )}
                {pack.usage_stage && (
                  <Badge variant="outline" className="text-[10px] px-1 py-0 border-rose-400 text-rose-600">
                    {pack.usage_stage === 'S1_4_LOCATOR' ? 'S1.5 Locator' : pack.usage_stage === 'S1_4_CROPPER' ? 'S1.5 Cropper' : pack.usage_stage}
                  </Badge>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
